import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { useGetAppFeatureMap } from 'src/api/app-features'
import { isAppFeatureEnabled, isAppFeatureIntegrationEnabled } from 'src/helpers/appFeatureHelper'
import { normalizeAppFeatureState } from 'src/helpers/appFeatureState'
import { useUserCtx } from 'src/hooks/context-hooks'

type FeatureFlagsContextValue = {
  flags: Record<string, boolean>
  integrations: Record<string, boolean>
  isLoading: boolean
  isEnabled: (key: string) => boolean
  /** Missing integration key = not configured in DB = allowed. */
  isIntegrationEnabled: (integrationKey: string) => boolean
}

const defaultValue: FeatureFlagsContextValue = {
  flags: {},
  integrations: {},
  isLoading: true,
  isEnabled: () => false,
  isIntegrationEnabled: () => true,
}

export const FeatureFlagsCtx = createContext<FeatureFlagsContextValue>(defaultValue)

export const FeatureFlagsProvider: React.FC = ({ children }) => {
  const { userCustomers } = useUserCtx()
  const customerIds = userCustomers?.map(c => c.id) ?? null
  const { data, isLoading } = useGetAppFeatureMap(customerIds)
  const state = normalizeAppFeatureState(data)
  const { features: flags, integrations } = state

  const isEnabled = useCallback((key: string) => isAppFeatureEnabled(key, flags), [flags])
  const isIntegrationEnabled = useCallback(
    (integrationKey: string) => isAppFeatureIntegrationEnabled(integrationKey, integrations),
    [integrations],
  )

  const value = useMemo(
    () => ({
      flags,
      integrations,
      isLoading,
      isEnabled,
      isIntegrationEnabled,
    }),
    [flags, integrations, isLoading, isEnabled, isIntegrationEnabled],
  )

  return <FeatureFlagsCtx.Provider value={value}>{children}</FeatureFlagsCtx.Provider>
}

export function useFeatureEnabled(key: string): boolean {
  const { isEnabled } = useContext(FeatureFlagsCtx)
  return isEnabled(key)
}
